import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { FooterComponent } from "./components/footer";
import {
  HomeNavigationComponent,
  NavigationComponent,
} from "./components/navigation";
import { StoreContextProvider } from "./store/store-context";
import { StyleProvider } from "./theme/provider";
import "bootstrap/dist/css/bootstrap.min.css";
import { ReactNotifications } from "react-notifications-component";
import { PageLoader } from "./components/loaders/pageloader.component";
import "./index.css";
import ContactUsProvider from "./providers/contactUs.provider";
import {
  BlogContentPage,
  BlogPage,
  CreateAccountPage,
  LazyComingSoonPage,
  LazyPrivacyPolicy,
  LazyTermsAndConditions,
  OurFarmPopUp,
  ShoppingCartPage,
  OurFarmsPage,
  ResetPasswordPage,
  SignInPage,
  SplitBuyDetailsPage,
  SplitBuyDealsPage,
  SettingsPage,
  SettingsSplitBuyHistoryPage,
  SettingsAddressManagementPage,
  SettingsPasswordChangePage,
  SettingsPersonalInformationPage,
  SettingsReferAndEarnPage,
  VerifyResetPasswordPage,
  CheckoutPage,
  GuestPage,
  OrderCompletePage,
  LazyRefundPolicy,
} from "./routes";
import { AuthLayout } from "./layouts/auth";
import {
  ProtectedForCartRouteProvider,
  ProtectedRouteProvider,
} from "./providers/protected-route.provider";
import { AuthenticationProvider } from "./providers/authentication.provider";
import { CartPage } from "./pages/shoppingCart";
import "leaflet/dist/leaflet.css";
import { CartProvider } from "./providers/cart.provider";
import { GoogleMapsProvider } from "./providers/google-map.provider";
import { useLocation } from "react-router";

export const RedirectTo = ({ redirectTo }) => (
  <Navigate to={redirectTo} replace />
);
const NavigationWrapper = () => {
  const location = useLocation();
  const isHomePage = [
    "/",
    "/blog",
    "/farms",
    "/refund-policy",
    "/privacy-policy",
    "/terms-and-conditions",
  ].includes(location.pathname);

  return isHomePage ? <HomeNavigationComponent /> : <NavigationComponent />;
};

function App() {
  return (
    <StoreContextProvider>
      <ReactNotifications />
      <Suspense fallback={<PageLoader />}>
        <GoogleMapsProvider>
          <StyleProvider>
            <BrowserRouter>
              <AuthenticationProvider>
                <CartProvider>
                  <ContactUsProvider>
                    <NavigationWrapper />
                    <main>
                      <Routes>
                        <Route path="/">
                          <Route index element={<LazyComingSoonPage />} />
                          <Route
                            path="split-buy"
                            element={<SplitBuyDealsPage />}
                          >
                            <Route
                              path="details/:split_buy_id"
                              element={<SplitBuyDetailsPage />}
                            />
                          </Route>
                          <Route
                            path="privacy-policy"
                            element={<LazyPrivacyPolicy />}
                          />
                          <Route
                            path="refund-policy"
                            element={<LazyRefundPolicy />}
                          />
                          <Route
                            path="terms-and-conditions"
                            element={<LazyTermsAndConditions />}
                          />
                          <Route path="farms" element={<OurFarmsPage />}>
                            <Route
                              path=":farm_slug"
                              element={<OurFarmPopUp />}
                            />
                          </Route>
                          <Route path="blog" element={<BlogPage />}></Route>
                          <Route element={<ProtectedRouteProvider />}>
                            <Route path={"settings"} element={<SettingsPage />}>
                              <Route
                                index
                                element={
                                  <Navigate replace to={"split-buy-history"} />
                                }
                              ></Route>
                              <Route
                                element={<SettingsSplitBuyHistoryPage />}
                                path={"split-buy-history"}
                              />
                              <Route
                                element={<SettingsAddressManagementPage />}
                                path={"address-management"}
                              />
                              <Route
                                element={<SettingsPasswordChangePage />}
                                path={"change-password"}
                              />
                              <Route
                                element={<SettingsPersonalInformationPage />}
                                path={"personal-information"}
                              />
                              <Route
                                element={<SettingsReferAndEarnPage />}
                                path={"refer-and-earn"}
                              />
                            </Route>
                            <Route
                              path="blog/:blogSection/:blogId"
                              element={<BlogContentPage />}
                            />
                          </Route>
                          <Route path={"cart"} element={<CartPage />}>
                            <Route index element={<ShoppingCartPage />}></Route>
                            <Route element={<ProtectedForCartRouteProvider />}>
                              <Route
                                path={"checkout"}
                                element={<CheckoutPage />}
                              ></Route>
                              <Route
                                path={"order-status"}
                                element={<OrderCompletePage />}
                              ></Route>
                            </Route>
                          </Route>
                        </Route>
                        <Route element={<AuthLayout />}>
                          <Route
                            path={"guest-sign-in"}
                            element={<GuestPage />}
                          />
                          <Route path={"sign-in"} element={<SignInPage />} />
                          <Route
                            path={"create-account"}
                            element={<CreateAccountPage />}
                          />
                          <Route
                            path={"reset-password"}
                            element={<ResetPasswordPage />}
                          />
                          <Route
                            path={"set-new-password"}
                            element={<VerifyResetPasswordPage />}
                          />
                        </Route>
                        <Route
                          path="*"
                          element={<RedirectTo redirectTo="/" />}
                        />
                      </Routes>
                    </main>
                    <FooterComponent />
                  </ContactUsProvider>
                </CartProvider>
              </AuthenticationProvider>
            </BrowserRouter>
          </StyleProvider>
        </GoogleMapsProvider>
      </Suspense>
    </StoreContextProvider>
  );
}

export default App;
